import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Pencil, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

export default function ExternalToolsSection() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ label: "", url: "", short: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/external-tools");
      setTools(r.data || []);
    } catch { setTools([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label.trim() || !form.url.trim()) {
      toast.error("Label and URL are required");
      return;
    }
    try {
      await api.post("/external-tools", form);
      setForm({ label: "", url: "", short: "" });
      toast.success("Tool added");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  const startEdit = (t) => { setEditing(t.id); setEditForm({ label: t.label, url: t.url, short: t.short || "" }); };
  const cancelEdit = () => { setEditing(null); setEditForm({}); };
  const saveEdit = async () => {
    try {
      await api.patch(`/external-tools/${editing}`, editForm);
      toast.success("Saved");
      cancelEdit();
      load();
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.label}"?`)) return;
    await api.delete(`/external-tools/${t.id}`);
    load();
  };

  return (
    <section data-testid="section-external-tools">
      <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit" }}>External Tools</h2>
      <p className="text-xs text-slate-500 mb-5">Quick-links shown in the sidebar — for tools your team uses every day (KDK, myBillBook, Zoho, Tally, etc.).</p>

      <div className="border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-2.5 w-20">Tile</th>
              <th className="text-left px-4 py-2.5">Label</th>
              <th className="text-left px-4 py-2.5">URL</th>
              <th className="px-4 py-2.5 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-slate-400">Loading…</td></tr>}
            {!loading && tools.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-slate-400">No tools yet — add one below.</td></tr>}
            {tools.map((t) => (
              <tr key={t.id} className="border-b border-slate-100" data-testid={`tool-row-${t.id}`}>
                {editing === t.id ? (
                  <>
                    <td className="px-4 py-2">
                      <Input value={editForm.short || ""} maxLength={3} onChange={(e) => setEditForm({ ...editForm, short: e.target.value })} className="rounded-sm w-16 text-center font-mono-data uppercase" data-testid={`edit-short-${t.id}`} />
                    </td>
                    <td className="px-4 py-2"><Input value={editForm.label || ""} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className="rounded-sm" data-testid={`edit-label-${t.id}`} /></td>
                    <td className="px-4 py-2"><Input value={editForm.url || ""} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} className="rounded-sm font-mono-data text-xs" data-testid={`edit-url-${t.id}`} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" className="h-7 bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={saveEdit} data-testid={`btn-save-tool-${t.id}`}><Check size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7" onClick={cancelEdit}><X size={12} /></Button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5">
                      <div className="h-7 w-7 rounded-[2px] border border-slate-300 bg-white inline-flex items-center justify-center text-[10px] font-bold text-slate-500">
                        {(t.short || (t.label || "?").slice(0, 2)).toUpperCase()}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium">{t.label}</td>
                    <td className="px-4 py-2.5">
                      <a href={t.url} target="_blank" rel="noreferrer" className="text-[#0a2540] hover:underline font-mono-data text-xs inline-flex items-center gap-1 truncate max-w-[300px]">
                        <ExternalLink size={11} /> <span className="truncate">{t.url}</span>
                      </a>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(t)} data-testid={`btn-edit-tool-${t.id}`}><Pencil size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(t)} data-testid={`btn-delete-tool-${t.id}`}><Trash2 size={12} /></Button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50/60">
              <td className="px-4 py-3">
                <Input
                  placeholder="KDK"
                  value={form.short}
                  onChange={(e) => setForm({ ...form, short: e.target.value })}
                  maxLength={3}
                  className="rounded-sm w-16 text-center font-mono-data uppercase"
                  data-testid="new-tool-short"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  placeholder="Tool name e.g., Spectrum Cloud"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  className="rounded-sm"
                  data-testid="new-tool-label"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  placeholder="https://…"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="rounded-sm font-mono-data text-xs"
                  data-testid="new-tool-url"
                />
              </td>
              <td className="px-4 py-3 text-right">
                <Button onClick={create} className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm h-9" data-testid="btn-add-tool">
                  <Plus size={12} className="mr-1" /> Add
                </Button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
